/**
 * Florida counties, loaded from the database. The surtax rate lives in
 * Postgres so it can be updated each January without a code change, and a
 * deal snapshots the rate it used at the time it was written.
 */

import { supabase } from "./supabase";

export interface County { name: string; surtax_bps: number }

let cache: County[] | null = null;

export async function loadCounties(): Promise<County[]> {
  if (cache) return cache;
  const { data, error } = await supabase
    .from("counties").select("name,surtax_bps").order("name");
  if (error) throw new Error("Could not load county tax rates.");
  cache = (data ?? []) as County[];
  return cache;
}

export function surtaxFor(counties: County[], name: string): number {
  return counties.find((c) => c.name === name)?.surtax_bps ?? 0;
}

/** Rate as a readable percentage: 50 -> "0.5%", 0 -> "none" */
export const bpsToPercent = (bps: number): string =>
  bps === 0 ? "none" : `${(bps / 100).toFixed(bps % 100 === 0 ? 0 : 1)}%`;

/**
 * The same rate, worded for a county picker. "Collier (none)" read like the
 * rate was missing rather than genuinely zero, so say what zero means: a
 * handful of Florida counties — Collier among them — levy no discretionary
 * sales surtax at all, and a buyer there pays the 6% state rate only.
 */
export const surtaxLabel = (bps: number): string =>
  bps === 0 ? "no surtax" : `${bpsToPercent(bps)} surtax`;
